import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    ActionRequiredCount,
    MobileNotification,
    MobileNotificationDetail,
    MobileNotificationState,
    PhpClientPaginatedResponse,
} from "../../../types";
import { getActionRequiredCount, getNotificationDetail, getNotifications, getNotificationCategories } from "./api";

// Load cached categories from localStorage
const getCachedCategories = () => {
    try {
        const cached = localStorage.getItem('notificationCategories');
        return cached ? JSON.parse(cached) : [];
    } catch (error) {
        console.error('Error loading cached categories:', error);
        return [];
    }
};

const initialState: MobileNotificationState = {
    isNotificationsLoading: false,
    notifications: [],

    isCategoriesLoading: false,
    categories: getCachedCategories(),

    isNotificationDetailLoading: false,
    fetchedNotificationDetails: undefined,
    selectedNotification: undefined,

    isActionRequiredCountLoading: false,
    actionRequiredCount: {
        total: 0,
        by_category: {},
    },
    actionRequiredCountError: null,
};

// Helper function to parse JSON safely
const parseJSONSafely = (data: any) => {
    try {
        return typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
        console.error("Error parsing JSON:", error);
        return data;
    }
};

export const mobileNotificationSlice = createSlice({
    name: "mobileNotifications",
    initialState: initialState,
    reducers: {
        setMobileNotifications: (state, { payload }) => {
            state.notifications = payload;
        },

        clearMobileNotifications: (state) => {
            state.notifications = [];
        },

        clearFetchedNotificationDetails: (state) => {
            state.fetchedNotificationDetails = undefined;
        },
        setSelectedNotification: (state, action: PayloadAction<MobileNotification | undefined>) => {
            state.selectedNotification = action.payload;
        },
    },
    extraReducers: {
        // get notifications
        [getNotifications.pending.type]: (state) => {
            state.isNotificationsLoading = true;
        },

        [getNotifications.fulfilled.type]: (
            state,
            action: PayloadAction<PhpClientPaginatedResponse<MobileNotification>>
        ) => {
            const { data, meta } = action.payload;
            state.isNotificationsLoading = false;

            // If it's page 1, replace the list (filter changed)
            // Otherwise, merge with existing (pagination/load more)
            if (meta?.page === 1) {
                // Page 1 means new filter - replace entire list
                state.notifications = data.sort((a, b) => {
                    const dateA = a.notification_date_time ? new Date(a.notification_date_time).getTime() : 0;
                    const dateB = b.notification_date_time ? new Date(b.notification_date_time).getTime() : 0;
                    return dateB - dateA; // Descending order
                });
            } else {
                // Page > 1 means load more - merge with existing
                const notificationMap = new Map(state.notifications.map((n) => [n.notification_id, n]));

                for (const incoming of data) {
                    if (!incoming.notification_id) continue;

                    if (notificationMap.has(incoming.notification_id)) {
                        // Update existing notification
                        notificationMap.set(incoming.notification_id, {
                            ...notificationMap.get(incoming.notification_id),
                            ...incoming, // merges fields from new data into the existing one
                        });
                    } else {
                        // Add new notification
                        notificationMap.set(incoming.notification_id, incoming);
                    }
                }

                state.notifications = Array.from(notificationMap.values()).sort((a, b) => {
                    const dateA = a.notification_date_time ? new Date(a.notification_date_time).getTime() : 0;
                    const dateB = b.notification_date_time ? new Date(b.notification_date_time).getTime() : 0;
                    return dateB - dateA; // Descending order
                });
            }
            
            state.notificationsMeta = meta;
        },

        [getNotifications.rejected.type]: (state) => {
            state.isNotificationsLoading = false;
        },

        // get notification details
        [getNotificationDetail.pending.type]: (state) => {
            state.isNotificationDetailLoading = true;
        },

        [getNotificationDetail.fulfilled.type]: (state, { payload }) => {
            state.isNotificationDetailLoading = false;
            const parsedNotificationDetail: MobileNotificationDetail = {
                ...payload,
                notification_param_json: parseJSONSafely(payload.notification_param_json),
                alert_json: parseJSONSafely(payload.alert_json),
            };
            state.fetchedNotificationDetails = parsedNotificationDetail;
        },

        [getNotificationDetail.rejected.type]: (state) => {
            state.isNotificationDetailLoading = false;
        },

        // get notification categories
        [getNotificationCategories.pending.type]: (state) => {
            state.isCategoriesLoading = true;
        },

        [getNotificationCategories.fulfilled.type]: (state, action: PayloadAction<any[]>) => {
            state.isCategoriesLoading = false;
            state.categories = action.payload;
            // Cache categories in localStorage
            try {
                localStorage.setItem('notificationCategories', JSON.stringify(action.payload));
            } catch (error) {
                console.error('Error caching categories:', error);
            }
        },

        [getNotificationCategories.rejected.type]: (state) => {
            state.isCategoriesLoading = false;
        },

        [getActionRequiredCount.pending.type]: (state) => {
            state.isActionRequiredCountLoading = true;
            state.actionRequiredCountError = null;
        },

        [getActionRequiredCount.fulfilled.type]: (state, action: PayloadAction<ActionRequiredCount>) => {
            state.isActionRequiredCountLoading = false;
            state.actionRequiredCount = action.payload || {
                total: 0,
                by_category: {},
            };
            state.actionRequiredCountError = null;
        },

        [getActionRequiredCount.rejected.type]: (state, action) => {
            state.isActionRequiredCountLoading = false;
            state.actionRequiredCountError = action.payload || action.error.message || "Failed to load action required count";
        },
    },
});

export const { setSelectedNotification } = mobileNotificationSlice.actions;
export default mobileNotificationSlice.reducer;
